// Purpose: admin audit log viewer. Search by action / actor / target /
// date range + cursor-paginated table. Chain integrity ✓/✗ indicator per
// row; CSV export triggers a download of audit rows + chain-proof JSON.
//
// Phase 7.5 port: PageHeader + Card-wrapped filter form with Input
// primitives + DataTable for results.
//
// Commit 8b.11: actor + recipient + IP-fingerprint display polish.
// Reads payload._actor (denormalized at write time by PostgresAuditLogger)
// to show "First Last <email> · ROLE" instead of a raw UUID. Recipient-
// notification verbs surface payload.recipient_email in the target column
// so the operator can see who was notified without drilling. IP column
// renamed to "IP fingerprint" with a copy-button so the hash can be
// pasted into log searches that correlate by hash.

import { useState } from 'react';
import type { AuditFilter, AuditPage, AuditRow } from '../../api/admin-audit.ts';
import { downloadAuditCsv, searchAudit } from '../../api/admin-audit.ts';
import { CATEGORIES, actionLabel } from '../../lib/audit-verbs.ts';
import { formatDateTime } from '../../lib/format.ts';
import { DownloadIcon } from '../../components/icons/index.tsx';
import { PageHeader } from '../../components/nav/PageHeader.tsx';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  MultiSelect,
  type Column,
} from '../../components/ui/index.ts';
import { AuditDetailModal } from './AuditDetail.tsx';

function shortHash(v: string | null | undefined): string {
  if (!v) return '—';
  return v.length > 10 ? `${v.slice(0, 8)}…` : v;
}

// Commit 8b.12: friendly verb labels live in apps/web/src/lib/audit-verbs.ts
// alongside the categorization used by the multi-select filter, so a new
// verb only needs to be registered in one place. Imported above as
// `actionLabel` — re-exported here is a no-op.

interface DenormalizedActor {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

function readActor(payload: unknown): DenormalizedActor | null {
  if (!payload || typeof payload !== 'object') return null;
  const inner = (payload as { payload?: unknown }).payload;
  // Audit rows store the canonical wrapper (action, actorType, ..., payload),
  // so the user-supplied payload is one level in. Be tolerant of either
  // shape so this works against both wire formats.
  const candidate =
    inner && typeof inner === 'object'
      ? (inner as { _actor?: unknown })._actor
      : (payload as { _actor?: unknown })._actor;
  if (!candidate || typeof candidate !== 'object') return null;
  const a = candidate as Partial<DenormalizedActor>;
  if (!a.id || !a.email) return null;
  return {
    id: a.id,
    email: a.email,
    displayName: a.displayName ?? a.email,
    role: a.role ?? '',
  };
}

function readPayloadString(payload: unknown, key: string): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const inner = (payload as { payload?: unknown }).payload;
  const obj = inner && typeof inner === 'object' ? inner : payload;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : null;
}

const RECIPIENT_VERBS = new Set([
  'recipient.notification.link_sent',
  'recipient.notification.code_sent',
  'recipient.notification.failed',
]);

// Commit 8b.12: action multi-select groups, derived once at module init
// from the canonical CATEGORIES table so a new verb registered in
// audit-verbs.ts surfaces in the dropdown without touching this page.
const ACTION_GROUPS = Object.entries(CATEGORIES).map(([label, options]) => ({
  label,
  options: options.map((o) => ({ value: o.verb, label: o.label })),
}));

export function Audit() {
  const [filter, setFilter] = useState<AuditFilter>({});
  const [page, setPage] = useState<AuditPage | null>(null);
  const [prevCursors, setPrevCursors] = useState<(string | null)[]>([null]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Commit 8b.12: drill-down modal state. Open when set; null when closed.
  const [detailRowId, setDetailRowId] = useState<string | null>(null);

  const run = async (cursor: string | null): Promise<void> => {
    setWorking(true);
    setError(null);
    try {
      const p = await searchAudit(filter, cursor ?? undefined);
      setPage(p);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  };

  const onSearch = async (): Promise<void> => {
    setPrevCursors([null]);
    await run(null);
  };

  const onNext = async (): Promise<void> => {
    if (!page?.nextCursor) return;
    setPrevCursors((prev) => [...prev, page.nextCursor]);
    await run(page.nextCursor);
  };

  const onPrev = async (): Promise<void> => {
    if (prevCursors.length <= 1) return;
    const next = prevCursors.slice(0, -1);
    setPrevCursors(next);
    await run(next[next.length - 1] ?? null);
  };

  const onExport = async (): Promise<void> => {
    try {
      await downloadAuditCsv(filter);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const columns: Column<AuditRow>[] = [
    {
      key: 'seq',
      header: 'Seq',
      width: '80px',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setDetailRowId(r.id)}
          className="font-mono text-body-s text-accent-primary hover:underline"
          aria-label={`Open audit row ${r.sequenceNumber}`}
        >
          {r.sequenceNumber}
        </button>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      cell: (r) => <span className="text-body-s text-fg-primary">{actionLabel(r.action)}</span>,
    },
    {
      key: 'actor',
      header: 'Actor',
      hideOnMobile: true,
      cell: (r) => {
        const actor = readActor(r.payload);
        if (actor) {
          return (
            <span className="text-body-s text-fg-secondary">
              <span className="text-fg-primary">{actor.displayName}</span>{' '}
              <span className="text-fg-muted">&lt;{actor.email}&gt;</span>
              {actor.role ? (
                <Badge tone="neutral" className="ml-2">
                  {actor.role}
                </Badge>
              ) : null}
            </span>
          );
        }
        if (r.actorType === 'system') {
          return <span className="text-body-s text-fg-muted italic">system</span>;
        }
        if (r.actorType === 'external') {
          // Anonymous recipient or external uploader. By design no
          // identity is captured — the dropbox token / share token is
          // the actor capability. NOT "legacy"; this is the canonical
          // shape for external actions. (8b.14 fix.)
          return <span className="text-body-s text-fg-muted italic">external recipient</span>;
        }
        // Pre-8b.11 user-actor row: only the raw UUID survives. Tag as
        // legacy so the operator knows why the name+email aren't here.
        return (
          <span className="font-mono text-body-s text-fg-secondary">
            {shortHash(r.actorId)}
            <span className="ml-2 text-fg-muted not-italic text-label">(legacy)</span>
          </span>
        );
      },
    },
    {
      key: 'target',
      header: 'Target',
      hideOnMobile: true,
      cell: (r) => {
        // Recipient-notification rows: surface the recipient email from
        // the payload so the operator can see who was notified without
        // drilling. Falls back to raw target if payload doesn't have it
        // (legacy rows pre-8b.11).
        if (RECIPIENT_VERBS.has(r.action)) {
          const recipient = readPayloadString(r.payload, 'recipient_email');
          if (recipient) {
            return (
              <span className="text-body-s text-fg-secondary">
                <span className="font-mono text-fg-muted">share:{shortHash(r.targetId)}</span>
                <span className="mx-1 text-fg-muted">→</span>
                <span className="text-fg-primary">{recipient}</span>
              </span>
            );
          }
        }
        return (
          <span className="font-mono text-body-s text-fg-secondary">
            {r.targetType ? `${r.targetType}:` : ''}
            {shortHash(r.targetId)}
          </span>
        );
      },
    },
    {
      key: 'when',
      header: 'When',
      hideOnMobile: true,
      cell: (r) => (
        <span className="text-body-s text-fg-muted">{formatDateTime(r.occurredAt)}</span>
      ),
    },
    {
      key: 'ip',
      header: 'IP fingerprint',
      hideOnMobile: true,
      cell: (r) =>
        r.ipHash ? (
          <span className="inline-flex items-center gap-1.5">
            <span
              className="font-mono text-body-s text-fg-muted"
              title={`One-way SHA-256 hash of the source IP. Plaintext IPs are intentionally not retained; correlate by hash across logs. Full hash: ${r.ipHash}`}
            >
              {shortHash(r.ipHash)}
            </span>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => void navigator.clipboard.writeText(r.ipHash ?? '')}
              aria-label="Copy IP fingerprint"
            >
              copy
            </Button>
          </span>
        ) : (
          <span className="text-body-s text-fg-muted">—</span>
        ),
    },
    {
      key: 'chain',
      header: 'Chain',
      width: '100px',
      cell: (r) =>
        r.chainOk ? <Badge tone="success">OK</Badge> : <Badge tone="danger">Broken</Badge>,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Audit search"
        actions={
          <Button
            variant="secondary"
            size="md"
            leftIcon={<DownloadIcon size={16} />}
            onClick={() => void onExport()}
          >
            Export CSV + chain-proof
          </Button>
        }
      />

      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Commit 8b.12: multi-select replaces free-text comma-separated
              entry. Verbs grouped by category come from audit-verbs.ts. */}
          <div className="flex flex-col gap-2">
            <label className="text-label uppercase text-fg-secondary">Actions</label>
            <MultiSelect
              groups={ACTION_GROUPS}
              selected={filter.actions ?? []}
              onChange={(next) => {
                const { actions: _drop, ...rest } = filter;
                void _drop;
                setFilter(next.length > 0 ? { ...rest, actions: next } : rest);
              }}
              placeholder="All actions"
              prefix="Verbs:"
            />
          </div>
          <Input
            label="Actor ID"
            value={filter.actorId ?? ''}
            onChange={(e) => setFilter({ ...filter, actorId: e.target.value })}
          />
          <Input
            label="Target ID"
            value={filter.targetId ?? ''}
            onChange={(e) => setFilter({ ...filter, targetId: e.target.value })}
          />
          <Input
            label="Target type"
            value={filter.targetType ?? ''}
            onChange={(e) => setFilter({ ...filter, targetType: e.target.value })}
          />
          <Input
            label="Start (ISO)"
            placeholder="2026-04-01T00:00:00Z"
            value={filter.startDate ?? ''}
            onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
          />
          <Input
            label="End (ISO)"
            placeholder="2026-04-30T00:00:00Z"
            value={filter.endDate ?? ''}
            onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
          />
          <div className="col-span-full flex items-center justify-end gap-2">
            <Button variant="primary" size="md" loading={working} onClick={() => void onSearch()}>
              Search
            </Button>
          </div>
        </div>
      </Card>

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {page ? (
        <div className="flex flex-col gap-3">
          <DataTable<AuditRow>
            columns={columns}
            rows={page.rows}
            getRowKey={(r) => r.id}
            caption="Audit log rows"
          />
          <div className="flex items-center justify-between text-body-s text-fg-secondary">
            <span>{page.rows.length} rows</span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void onPrev()}
                disabled={working || prevCursors.length <= 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void onNext()}
                disabled={working || !page.nextCursor}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Commit 8b.12: drill-down modal. Click any seq number in the
          table or in the modal's related-rows list to open. */}
      <AuditDetailModal
        rowId={detailRowId}
        onClose={() => setDetailRowId(null)}
        onNavigate={(id) => setDetailRowId(id)}
      />
    </div>
  );
}
